import { useCallback, useMemo, useEffect } from 'react'
import cn from 'classnames/bind'
import moment from 'moment-timezone'
import { objOf, F, reduce } from 'ramda'

import PlainInputMasked, { PlainInputMaskedProps } from '../plain-input-masked/plain-input-masked'
import { maskType } from '../plain-input-masked/mask-types'
import PlainDropdownSurface, { SurfaceType } from '../plain-dropdown-surface'
import PlainIcon, { iconTypes, colorTypes } from '../plain-icon'
import PlainCalendar from '../plain-calendar'
import { dateFormat as dateFormatDefault } from './date-format'
import usePlainInputDate from './use-plain-input-date'
import { dispatchFormChanged } from '../custom-events'

import styles from './plain-input-date.module.scss'

const cx = cn.bind(styles)

export type PlainInputDateProps = {
  className?: string
  dateDisabledStatus?: (dayFormatted: string, format: string, unit?: string) => boolean
  onDateChange: (value: string, id?: string) => void
  markedDatesFrom?: string
  markedDatesTo?: string
  calendarValues?: Array<string> | null
  calendarCursor?: () => string // возвращает дату, на которую должно открываться выпадающее окно с календарем
  calendarCloseOnClick?: boolean
  displayAsBlock?: boolean
  dateFormat?: string
  hideYear?: boolean
  defaultValue?: string
  disableClearInvalidDate?: boolean
  disableMonthSwitcherByStatus?: boolean
  getUnlockedDate?: (lockedDate: string, dateFormat: string, itemId?: string) => string | null
} & Omit<PlainInputMaskedProps, 'predefinedMaskType'>

const PlainInputDate = ({
  id,
  className,
  dateDisabledStatus,
  onDateChange,
  markedDatesFrom,
  markedDatesTo,
  calendarValues,
  calendarCursor,
  calendarCloseOnClick = true,
  displayAsBlock,
  dateFormat = dateFormatDefault,
  hideYear,
  value: valueIncoming,
  disabled,
  defaultValue,
  disableClearInvalidDate,
  disableMonthSwitcherByStatus,
  getUnlockedDate,
  ...others
}: PlainInputDateProps) => {
  const [value, dateValueChange, onBlur] = usePlainInputDate({
    id,
    valueIncoming,
    dateFormat,
    onChange: onDateChange,
    defaultValue,
    disableClearInvalidDate,
  })

  const dateClick = useCallback(
    (toggle: () => void) => (date: string, dayFormat: string) => {
      dateValueChange({
        formattedValue: date,
        valid: dateDisabledStatus ? !dateDisabledStatus(date, dayFormat) : true,
        dayFormat,
      })
      if (calendarCloseOnClick) {
        toggle()
      }
    },
    [dateValueChange, dateDisabledStatus, calendarCloseOnClick],
  )

  const handleValueChange = useCallback(
    ({ formattedValue: val }, { source }) => {
      if (source === 'event') {
        // только события изменения пользователем
        const mDate = moment(val, dateFormatDefault)
        const disabledStatus = dateDisabledStatus || F
        const valid = val.length === dateFormatDefault.length && mDate.isValid()
        dateValueChange({
          formattedValue: val,
          valid: valid && !disabledStatus(val, dateFormatDefault),
          dayFormat: dateFormatDefault,
        })
        dispatchFormChanged()
      }
    },
    [dateValueChange, dateDisabledStatus],
  )

  const daysSelected = useMemo(() => {
    if (calendarValues) {
      return reduce(
        (acc, val) => {
          acc[val] = val
          return acc
        },
        {},
        calendarValues,
      )
    }
    if (value) {
      const mValue = moment(value, dateFormatDefault)
      if (mValue.isValid()) {
        const fromValue = mValue.format(dateFormatDefault)
        return objOf(fromValue, fromValue)
      }
    }
    return {}
  }, [value, calendarValues])

  useEffect(() => {
    if (
      valueIncoming !== undefined &&
      getUnlockedDate &&
      dateDisabledStatus &&
      !disabled &&
      dateDisabledStatus(valueIncoming, dateFormat, 'day')
    ) {
      const unlockedDate = getUnlockedDate(valueIncoming, dateFormat, id)
      if (unlockedDate && unlockedDate !== valueIncoming) {
        onDateChange(unlockedDate, id)
      }
    }
  }, [getUnlockedDate, disabled, dateDisabledStatus, valueIncoming, dateFormat, id]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderCalendar = (toggle: () => void) => (
    <PlainCalendar
      onDayClick={dateClick(toggle)}
      daysSelected={daysSelected}
      calendarCursor={calendarCursor}
      dateFormat={dateFormatDefault}
      dateDisabledStatus={dateDisabledStatus}
      disableMonthSwitcherByStatus={disableMonthSwitcherByStatus}
      markedDatesFrom={markedDatesFrom}
      markedDatesTo={markedDatesTo}
      hideYear={hideYear}
    />
  )

  return (
    <div className={cx(styles.root, { displayAsBlock, hideYear }, className)}>
      <PlainInputMasked
        id={id}
        name={id}
        value={value}
        onBlur={onBlur}
        disabled={disabled}
        {...others}
        onValueChange={handleValueChange}
        predefinedMaskType={hideYear ? maskType.MASK_DAY_MONTH : maskType.MASK_DATE}
      />
      <div className={styles.calendar}>
        <PlainDropdownSurface id="date" type={SurfaceType.Grid} surfaceContent={renderCalendar}>
          {(onClick, _, areaProps) => (
            <button
              type="button"
              className={cx(styles.button, { disabled })}
              onClick={onClick}
              disabled={disabled}
              {...areaProps}
            >
              <PlainIcon
                type={iconTypes.CALENDAR}
                colorType={disabled ? colorTypes.COLOR_GRAY : colorTypes.COLOR_PRIMARY}
                width={18}
                height="auto"
              />
            </button>
          )}
        </PlainDropdownSurface>
      </div>
    </div>
  )
}

export default PlainInputDate
